import { ListAlt } from '@mui/icons-material';
import { Box, Button, Popover, Typography } from '@mui/material';
import React from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import { truncateTo2dp } from 'utils/helpers/general';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { ScrollableListPopover } from '../../../Common/ScrollableListPopoverForTreeCharts';
import type { CategoryData } from '../utils';
import { CustomizedContent } from './TreeChartCellRenderer';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const COLORS = ['#4683E6'];

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any;
  label?: string;
  enableDarkTheme: boolean;
}> = ({ active, payload, label, enableDarkTheme }) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const hoverCell = payload[0].payload;

    return (
      <>
        <Box
          sx={{
            backgroundColor: enableDarkTheme
              ? palette.pageBackground
              : palette.pageBackground,
            padding: '10px',
          }}
        >
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('component')} {hoverCell?.name || ''}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('total_cost_2')} {truncateTo2dp(hoverCell.totalCost)}{' '}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('total_hours')} {hoverCell.totalHours}{' '}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('total_part_count')} {hoverCell.totalWorkOrders}{' '}
          </Typography>
        </Box>
      </>
    );
  }

  return null;
};

const ListViewButton = ({
  data,
  enableDarkTheme,
}: {
  data: CategoryData[];
  enableDarkTheme?: boolean;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleOpenList = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseList = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Box display="flex" justifyContent="flex-end" mb={1}>
      <Button
        sx={{
          color: palette.textPrimary,
          border: `1px solid ${palette.primary}`,
          textTransform: 'none',
          height: '30px',
        }}
        onClick={handleOpenList}
        startIcon={<ListAlt />}
      >
        {t('list_view')}
      </Button>
      {open && (
        <ScrollableListPopover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseList}
          data={data}
          getPrimaryText={(item) => `${item.name}`}
          getSecondaryText={(item) => `Total Cost: ${item.totalCost}`}
          enableDarkTheme={enableDarkTheme}
          width={200}
          height={400}
        />
      )}
    </Box>
  );
};

export const ComponentsTreeChart = ({
  selectedCell,
  handleCellClick,
  data,
  enableDarkTheme = false,
  open,
  anchorEl,
  handleClose,
  showPopOver = false,
  handleShowTab,
}: {
  selectedCell: string;
  handleCellClick: any;
  data: CategoryData[];
  enableDarkTheme: boolean;
  open: boolean;
  anchorEl: any;
  handleClose: any;
  showPopOver?: boolean;
  handleShowTab: any;
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const id = open ? 'simple-popover' : undefined;

  const handleConfirmButtonClick = (type: string, name: string) => {
    handleShowTab(type, name);
    handleClose();
  };

  const cellData = data.find((item) => item.name === selectedCell);
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ListViewButton data={data} enableDarkTheme={enableDarkTheme} />
      <ResponsiveContainer width="100%" height={400}>
        <Treemap
          // width={500}
          // height={600}
          data={data}
          dataKey="totalCostForDisplay"
          aspectRatio={4 / 3}
          stroke="#fff"
          content={
            // @ts-expect-error error exists in ts with chart library
            <CustomizedContent
              colors={COLORS}
              onClick={handleCellClick}
              selectedCell={selectedCell}
              enableDarkTheme={enableDarkTheme}
              primaryColor={palette.primary}
            />
          }
        >
          <Tooltip
            content={<CustomTooltip enableDarkTheme={enableDarkTheme} />}
          />
        </Treemap>
      </ResponsiveContainer>
      {showPopOver && open && (
        <Popover
          id={id}
          open={showPopOver && open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box
            sx={{
              backgroundColor: palette.paperBackground,
              minHeight: '40px',
              minWidth: '150px',
              color: palette.textPrimary,
            }}
          >
            {cellData ? (
              <>
                <Typography
                  sx={{
                    fontSize: '12px',
                    textAlign: 'center',
                    backgroundColor: palette.primary,
                    paddingTop: '5px',
                    paddingBottom: '5px',
                  }}
                >
                  {capitalizeFirstLetter(cellData.name)}
                </Typography>
                <Box
                  sx={{
                    padding: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#7B7B79',
                      marginTop: '5px',
                    }}
                  >
                    {t('total_cost_2')}{' '}
                    <span>${formatNumberToShortScale(cellData.totalCost)}</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#7B7B79',
                      marginTop: '5px',
                    }}
                  >
                    {t('total_hours')}{' '}
                    <span>{formatNumberToShortScale(cellData.totalHours)}</span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#7B7B79',
                      marginTop: '5px',
                    }}
                  >
                    {t('total_part_count')}{' '}
                    <span>
                      {formatNumberToShortScale(cellData.totalWorkOrders)}
                    </span>
                  </Typography>
                </Box>
              </>
            ) : null}
            <Box>
              <Button
                onClick={() => {
                  handleConfirmButtonClick('subcomponent', selectedCell);
                }}
                color="primary"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  width: '100%',
                }}
              >
                {t('view_subcomponents')}
              </Button>
            </Box>
          </Box>
        </Popover>
      )}
    </div>
  );
};
